import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery, useTheme } from "@mui/material";

import { Task } from "Models/task";
import { TaskDate } from "Models/sales";
import { useAppState } from "Context/StateContext";
import { TaskType, TaskStatus } from "Util/enums";
import { groupArrayBy } from "Util/groupBy";
import { TaskDateUtils } from "Util/dateUtils";

import { TaskDateComponent } from "../../TaskDate";
import { TypeDate } from "../../utils/types";

import { DividerStyle, ContainerStackTaskDates, GridItem, SummaryTitle } from "./styles";

interface TaskSummaryProps {
  tasks?: Task[];
  taskDates?: TaskDate[];
  isViewMode?: boolean;
  onUpdate?: (tasks?: Task[], taskDates?: TaskDate[]) => void;
}

export function TaskSummary(props: TaskSummaryProps) {
  const { defaultTimezone, currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { tasks = [], taskDates = [], isViewMode = false, onUpdate } = props;

  const taskDateGrouped = useMemo(() => {
    if (!taskDates?.length) return null;

    return groupArrayBy([...taskDates.filter((task) => !!task?.valid)], "taskType.name");
  }, [taskDates]);

  const hasTaskToShow = useMemo(() => {
    if (!tasks?.length) return false;

    const taskVideoSurveyOrHomeSurvey = tasks.filter(
      (task) => task?.type === TaskType.VIDEO_SURVEY || task?.type === TaskType.HOME_SURVEY
    );
    const taskDateGroupedPrefeerUplift = taskDateGrouped?.[TaskType.PREFERRED_UPLIFT] || [];
    return taskVideoSurveyOrHomeSurvey.length > 0 || taskDateGroupedPrefeerUplift.length > 0;
  }, [taskDateGrouped, tasks]);

  const hasTaskDateToShow = useMemo(() => {
    if (!taskDateGrouped) return false;

    const keyGrouped = Object.getOwnPropertyNames(taskDateGrouped);
    if (!keyGrouped?.length) return false;

    const keysThatAreNotPreferredUpliftTask = keyGrouped.filter(
      (key) => key !== TaskType.PREFERRED_UPLIFT
    );

    return keysThatAreNotPreferredUpliftTask.length > 0;
  }, [taskDateGrouped]);

  const hasAnyTasksToShow = useMemo(() => {
    return hasTaskToShow || hasTaskDateToShow;
  }, [hasTaskToShow, hasTaskDateToShow]);

  const onAddTaskDate = (type: TaskType, dates: TypeDate[]) => {
    if (!dates?.length) return;

    const taskDatesArr = [...taskDates];
    const tasksArr = [...tasks];

    dates?.forEach((date) => {
      if (date?.checked) {
        if (date?.id) {
          const index = taskDatesArr?.findIndex((taskDate) => taskDate.id === date.id);

          if (index !== -1) {
            taskDatesArr[index] = {
              ...taskDatesArr[index],
              valid: false,
            };
          }
        }

        tasksArr.push({
          type,
          startDate: date.value,
          status: TaskStatus.REGISTERED,
          companyId: currentUser?.companyId,
        } as Task);
      }
    });

    onUpdate?.(tasksArr, taskDatesArr);
  };

  const onChangeTaskDate = (dates: TypeDate[]) => {
    if (!dates?.length) return;

    const taskDatesArr = [...taskDates];

    dates.forEach((date) => {
      if (date?.id) {
        const index = taskDatesArr?.findIndex((taskDate) => taskDate.id === date.id);

        if (index !== -1) {
          taskDatesArr[index] = {
            ...taskDatesArr[index],
            checked: date?.checked || false,
          };
        }
      }
    });

    onUpdate?.(undefined, taskDatesArr);
  };

  const sortAndFormatTasksByType = (type?: TaskType) => {
    if (!type || !tasks) return null;

    const filteredTasks = tasks?.filter(
      (task) =>
        task.type === type && !!task.startDate && TaskDateUtils.isValidDate(task.startDate)
    );
    return filteredTasks
      ?.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
      .map((task) => ({
        value: task.startDate,
        formattedDate: TaskDateUtils.formatForDisplay(task.startDate, "dd/MM/yyyy", {
          timezone: defaultTimezone,
        }),
        formattedHour: TaskDateUtils.formatForDisplay(task.startDate, "HH:mm", {
          timezone: defaultTimezone,
        }),
        checked: false,
      }));
  };

  const formatTaskDateByType = (type?: TaskType) => {
    if (!type || !taskDateGrouped?.[type]) return null;

    return taskDateGrouped?.[type]
      ?.filter((task: TaskDate) => !!task.date && TaskDateUtils.isValidDate(task.date))
      ?.map((task: TaskDate) => ({
        id: task.id,
        value: task.date,
        formattedDate: TaskDateUtils.formatForDisplay(task.date!, "dd/MM/yyyy", {
          timezone: defaultTimezone,
        }),
        checked: false,
      }));
  };

  const renderInfoTask = (
    type?: TaskType,
    isDiffOfHomeOrVideoSurvey = false,
    useActions = false
  ) => {
    const dates = isDiffOfHomeOrVideoSurvey
      ? formatTaskDateByType(type)
      : sortAndFormatTasksByType(type);

    if (!dates) return null;

    return (
      <GridItem
        key={type}
        container
        direction={mobile ? "column" : "row"}
        xs={mobile ? 12 : 4}
        justifyContent="space-between"
      >
        <TaskDateComponent
          hideAddDatesSelected
          type={type}
          dates={dates}
          onAddTaskDates={(dates) => (useActions ? onAddTaskDate(type!, dates) : undefined)}
          onChangeDates={(dates) => (useActions ? onChangeTaskDate(dates) : undefined)}
        />
        <DividerStyle flexItem orientation={mobile ? "horizontal" : "vertical"} isMobile={mobile} />
      </GridItem>
    );
  };

  const renderTaskDates = () => {
    if (!taskDateGrouped) return null;

    const groupKeys = Object.getOwnPropertyNames(taskDateGrouped);

    if (!groupKeys?.length) return null;

    const keysThatAreNotPreferredUpliftTask = groupKeys.filter(
      (key) => key !== TaskType.PREFERRED_UPLIFT
    );

    if (!keysThatAreNotPreferredUpliftTask?.length) return null;

    return keysThatAreNotPreferredUpliftTask.map((key) => {
      return renderInfoTask(key as TaskType, true, true);
    });
  };

  return (
    <section>
      {!isViewMode && hasTaskDateToShow && <SummaryTitle>{translate("summaryFromSales")}</SummaryTitle>}
      {hasTaskToShow && (
        <ContainerStackTaskDates container direction={mobile ? "column" : "row"}>
          {renderInfoTask(TaskType.HOME_SURVEY)}
          {renderInfoTask(TaskType.VIDEO_SURVEY)}
          {renderInfoTask(TaskType.PREFERRED_UPLIFT, true)}
        </ContainerStackTaskDates>
      )}
      {hasTaskDateToShow && (
        <ContainerStackTaskDates container direction={mobile ? "column" : "row"}>
          {renderTaskDates()}
        </ContainerStackTaskDates>
      )}
    </section>
  );
}
